import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import syncService from './services/databaseSync';
import { supabase } from './supabase';
import Header from './components/Header';
import Footer from './components/Footer';
import PrivateRoute from './components/PrivateRoute';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import DashboardPage from './pages/DashboardPage';
import JobRequestsPage from './pages/JobRequestsPage';
import WorkersPage from './pages/WorkersPage';
import AddWorkerPage from './pages/AddWorkerPage';
import MigrationPage from './pages/MigrationPage';
import AdminsPage from './pages/AdminsPage';
import ApplicationsPage from './pages/ApplicationsPage';
import './styles/variables.css';
import './styles/global.css';

const ENABLE_BIDIRECTIONAL_SYNC = process.env.REACT_APP_ENABLE_BIDIRECTIONAL_SYNC === 'true';

/**
 * Initializes Firestore/Supabase sync ONLY after the user is authenticated.
 * This prevents "Missing or insufficient permissions" errors on the login page.
 */
function SyncInitializer() {
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser || !ENABLE_BIDIRECTIONAL_SYNC) return;

    syncService.initializeBidirectionalSync();

    const runMigrationIfNeeded = async () => {
      const tables = ['customers', 'admins', 'serviceProviders', 'bookings', 'reviews'];
      for (const table of tables) {
        try {
          const { count, error } = await supabase
            .from(table)
            .select('*', { count: 'exact', head: true });

          if (error) {
            console.warn(`⚠️ Could not check Supabase table "${table}": ${error.message}`);
            await syncService.migrateFirebaseToSupabase();
            break;
          } else if (count === 0) {
            console.log(`🔄 "${table}" is empty in Supabase. Running migration...`);
            await syncService.migrateFirebaseToSupabase();
            break;
          } else {
            console.log(`✅ "${table}" has ${count} rows in Supabase.`);
          }
        } catch (err) {
          console.warn(`⚠️ Migration check for "${table}" failed:`, err.message);
        }
      }
    };

    runMigrationIfNeeded();

    return () => {
      syncService.stopAllListeners();
    };
  }, [currentUser]);

  return null;
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <SyncInitializer />
        <div className="App">
          <Header />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <DashboardPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/job-requests"
              element={
                <PrivateRoute>
                  <JobRequestsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/workers"
              element={
                <PrivateRoute>
                  <WorkersPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/add-worker"
              element={
                <PrivateRoute>
                  <AddWorkerPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/migration"
              element={
                <PrivateRoute>
                  <MigrationPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/admins"
              element={
                <PrivateRoute>
                  <AdminsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/applications"
              element={
                <PrivateRoute>
                  <ApplicationsPage />
                </PrivateRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Footer />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
